<template>
    <AppHead :title="blog.title" :description="blog.sub_title" :image="blog.cover_photo_path"></AppHead>
    <website-layout>
        <div id="blogpost"
             class="relative isolate overflow-hidden bg-white px-6 py-12 sm:py-32 lg:overflow-visible lg:px-0">
            <div class="absolute inset-0 -z-10 overflow-hidden">
                <svg
                    class="absolute left-[max(50%,25rem)] top-0 h-[64rem] w-[128rem] -translate-x-1/2 stroke-gray-200 [mask-image:radial-gradient(64rem_64rem_at_top,white,transparent)]"
                    aria-hidden="true">
                    <defs>
                        <pattern id="e813992c-7d03-4cc4-a2bd-151760b470a0" width="200" height="200" x="50%" y="-1"
                                 patternUnits="userSpaceOnUse">
                            <path d="M100 200V.5M.5 .5H200" fill="none"/>
                        </pattern>
                    </defs>
                    <svg x="50%" y="-1" class="overflow-visible fill-gray-50">
                        <path
                            d="M-100.5 0h201v201h-201Z M699.5 0h201v201h-201Z M499.5 400h201v201h-201Z M-300.5 600h201v201h-201Z"
                            stroke-width="0"/>
                    </svg>
                    <rect width="100%" height="100%" stroke-width="0"
                          fill="url(#e813992c-7d03-4cc4-a2bd-151760b470a0)"/>
                </svg>
            </div>
            <div class="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 lg:mx-0 lg:max-w-none lg:grid-cols-2 lg:items-start lg:gap-y-10">
                <div
                    class="lg:col-span-2 lg:col-start-1 lg:row-start-1 lg:mx-auto lg:grid lg:w-full lg:max-w-7xl lg:grid-cols-2 lg:gap-x-8 lg:px-8">
                    <div class="lg:pr-4">
                        <div class="lg:max-w-lg">
                            <h1 class="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                                {{ getTranslatedValue(blog, 'title') }}</h1>
                            <p class=" text-xl leading-8 text-gray-600" v-if="blog.sub_title">
                                {{ getTranslatedValue(blog, 'sub_title') }}</p>
                            <img class="rounded-xl shadow-xl ring-1 ring-gray-400/10 xl:hidden" :src="'/storage/'+blog.cover_photo_path" :alt="getTranslatedValue(blog, 'photo_alt_text')"/>
                            <div class="mt-6 text-xl leading-8 text-gray-700 flex flex-col gap-2">
                                <div v-for="(part, index) in renderedBody" :key="index">
                                    <component v-if="typeof part === 'object'" :is="part"></component>
                                    <div v-else v-html="part"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="sm:-ml-12 sm:-mt-12 sm:p-12 lg:sticky lg:top-4 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:overflow-hidden">
                    <img class="w-[48rem] max-w-none rounded-xl shadow-xl ring-1 ring-gray-400/10 sm:w-full hidden xl:block"
                         :src="'/storage/'+blog.cover_photo_path" :alt="getTranslatedValue(blog, 'photo_alt_text')"/>
                    <p>{{ trans('pages.blogs.writter_by', { name: blog.user.name}) }} {{ formatDate(blog.created_at) }}</p>
                    <div class="mt-8 card-body bg-white rounded shadow">
                        <h3>{{ trans('pages.blogs.other_posts') }}</h3>
                        <ul class="list-disc ml-8 my-2">
                            <li v-for="blog in otherBlogs" :key="blog.id" class="py-2">
                                <dl class="relative flex flex-wrap gap-x-3">
                                    <dd class="w-full flex-none font-semibold tracking-tight text-primary">
                                        <a :hreflang="getActiveLanguage()" :href="route('blog',{slug: getTranslatedValue(blog, 'slug')})" class="text-[#2fba98]">
                                            {{ getTranslatedValue(blog, 'title') }}
                                            <span class="absolute inset-0" aria-hidden="true"/>
                                        </a>
                                    </dd>
                                </dl>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
        <div class="relative isolate overflow-hidden bg-gray-900">
            <div class="px-6 py-24 sm:px-6 sm:py-32 lg:px-8">
                <div class="mx-auto max-w-2xl text-center">
                    <h2 class="text-3xl font-bold tracking-tight text-white sm:text-4xl">{{ trans('pages.blogs.on_search') }}</h2>
                    <p class="mx-auto mt-6 max-w-xl text-lg leading-8 text-gray-300">{{ trans('pages.blogs.on_search_desc') }}</p>
                    <div class="mt-10 flex items-center justify-center gap-x-6">
                        <a hreflang="nl" :href="route('configurator')" class="btn btn-outline text-white">{{ trans('pages.blogs.design_button') }}<span
                                aria-hidden="true">→</span></a>
                    </div>
                </div>
            </div>
            <svg viewBox="0 0 1024 1024"
                 class="absolute left-1/2 top-0 -z-10 h-[64rem] w-[64rem] -translate-x-1/2 [mask-image:radial-gradient(closest-side,white,transparent)]"
                 aria-hidden="true">
                <circle cx="512" cy="512" r="512" fill="url(#8d958450-c69f-4251-94bc-4e091a323369)" fill-opacity="0.7"/>
                <defs>
                    <radialGradient id="8d958450-c69f-4251-94bc-4e091a323369">
                        <stop stop-color="#7775D6"/>
                        <stop offset="1" stop-color="#E935C1"/>
                    </radialGradient>
                </defs>
            </svg>
        </div>

    </website-layout>
</template>


<script setup>
import moment from 'moment';
import {computed, h} from "vue";
import CTA1 from "@/Components/CTA1.vue";
import CTA2 from "@/Components/CTA2.vue";
import CTA3 from "@/Components/CTA3.vue";
import WebsiteLayout from "@layouts/website-layout.vue";
import AppHead from "@layouts/sections/app-head.vue";
import { trans, getActiveLanguage } from 'laravel-vue-i18n';
import { getTranslatedValue } from "@/Utils/TranslationUtils";

const props = defineProps({
    blog: {
        type: Object,
    },
    otherBlogs: {
        type: Array
    }

});


const replacements = {
    __CTA1__: () => h(CTA1),
    __CTA2__: () => h(CTA2),
    __CTA3__: () => h(CTA3),
};

function splitByArray(inputString, splitArray) {
    return splitArray.reduce((result, splitString) => {
        return result.reduce((newResult, str) => {
            const parts = str.split(splitString);
            for (let i = 0; i < parts.length - 1; i++) {
                newResult.push(parts[i], splitString);
            }
            newResult.push(parts[parts.length - 1]);
            return newResult;
        }, []);
    }, [inputString]);
}

function render() {
    return splitByArray(getTranslatedValue(props.blog, 'body'), Object.keys(replacements)).map((part) => {
        return replacements[part]?.() ?? part;
    });
}

const formatDate = (date) => {
    return moment(date).format('D/M/YYYY');
}

const getFirstParagraph = (html) => {
    let tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Find the first paragraph
    let firstParagraph = tempDiv.querySelector('p');

    // Strip HTML tags from the paragraph and get text
    if (firstParagraph) {
        let text = firstParagraph.innerText || firstParagraph.textContent;

        // Split the text into sentences
        let sentences = text.match(/[^\.!\?]+[\.!\?]+/g);

        // Return the first two sentences joined together
        if (sentences && sentences.length > 1) {
            return sentences.slice(0, 2).join(' ');
        } else if (sentences) {
            return sentences[0];
        }
    }
    return '';
}

const description = getFirstParagraph(props.blog.body);
const renderedBody = computed(() => render());
</script>

<style scoped lang="scss">
::v-deep(#blogpost) {
    h2 {
        margin-top: 1.3rem;
        font-size: 1.5rem;
        font-weight: 700;
        font-family: Cambay, sans-serif;
    }

    h4 {
        margin-top: 1rem;
        font-size: 1.8rem;
        font-weight: 700;
        font-family: Cambay, sans-serif;
    }

    img {
        border-radius: 0.3rem;
        margin: 1.3rem 0;
    }
}
</style>
